/**
 * Ad journey tracking for WordPress pages.
 *
 * Fires two Meta-only events for visitors who arrived from a paid Meta ad:
 *   get_started_cta_click   — clicked a button that leads to /get-started
 *   mobile_visitor_landing  — landed on a viewport too small to install the add-on
 *
 * Both go through facebookConversionEvent, so they dual-send browser + Conversions
 * API on a shared event id. They deliberately do NOT go through GTM — Meta events
 * are sent from code on this site so they inherit that deduplication.
 *
 * Mirrors src/components/Globals/Analytics/UtmTracker.tsx in coeff-headless-app.
 * Keep the two in step.
 */

import { getCookie, storeUTM, facebookConversionEvent } from './util.js?ver=2.4.0';

(function () {

	const MOBILE_BREAKPOINT = 1024;
	const LANDING_SESSION_FLAG = 'coeff_mobile_landing_fired';
	const META_SOURCES = ['meta', 'facebook', 'fb', 'instagram', 'ig'];
	const PAID_MEDIUMS = ['paid', 'cpc', 'ppc', 'paid_social', 'paidsocial'];
	// Matches Meta's default 7 day click attribution window, and the lifetime of
	// the UTM cookie, so both signals agree on how long someone counts as ad
	// sourced.
	const AD_SOURCE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

	/**
	 * WordPress pages only ran storeUTM on /redirect, so the UTM cookie was never
	 * written on a normal landing. Without it there is nothing to read the ad
	 * source from on a later page view.
	 */
	try {
		storeUTM();
	} catch (e) {
		console.log('ad-journey: failed to store utm params', e);
	}

	/**
	 * _fbc is `fb.1.<clicked_at_ms>.<fbclid>` and lives for 90 days, which is far
	 * wider than we want to call someone ad sourced. Read the click time out of it
	 * rather than treating mere presence as a signal.
	 *
	 * @param {string} fbc
	 * @returns {boolean}
	 */
	const fbcIsRecent = (fbc) => {
		const clickedAt = Number(fbc.split('.')[2]);
		if (!Number.isFinite(clickedAt)) return false;
		return Date.now() - clickedAt <= AD_SOURCE_MAX_AGE_MS;
	};

	/**
	 * The paid source when this visit came from a recent Meta ad, otherwise null.
	 *
	 * Read from the cookie rather than the URL: the CTA click usually happens on a
	 * page after the one the ad landed on, so the query string is long gone.
	 *
	 * @returns {string|null}
	 */
	const getAdSource = () => {
		// A recent Meta click id. Live ads set utm_medium=paidsocial_{{placement}} and
		// utm_source varies between them, so the click id is what reliably says "Meta"
		// while utm_medium is what reliably says "paid". Note Meta adds fbclid to
		// organic clicks (posts, shares, Messenger) too, which is why it is paired
		// with the medium check rather than trusted alone.
		const hasMetaClickId = () => {
			if (new URLSearchParams(window.location.search).get('fbclid')) return true;
			const fbc = getCookie('_fbc');
			return !!fbc && fbcIsRecent(fbc);
		};

		try {
			const cookieStr = getCookie('__coeff-utm2');
			if (cookieStr) {
				const parsed = JSON.parse(cookieStr);
				const utm = (parsed && parsed.utm_params) || {};
				const source = String(utm.utm_source || '').toLowerCase();
				const medium = String(utm.utm_medium || '').toLowerCase();

				// Prefix match, not equality: real values carry a placement suffix,
				// e.g. paidsocial_Facebook_Mobile_Feed.
				const isPaid = PAID_MEDIUMS.some(function (m) { return medium.indexOf(m) === 0; });
				const isMeta = META_SOURCES.indexOf(source) !== -1 || hasMetaClickId();

				// Normalise: live ads set utm_source={{campaign.name}} on some creatives,
				// so the raw value can be a campaign title. Only report a real source.
				if (isPaid && isMeta) return META_SOURCES.indexOf(source) !== -1 ? source : 'meta';

				// Tagged with a medium we know isn't paid — organic, email, referral.
				if (medium && !isPaid) return null;

				// No medium at all: fall through to the click id below.
			}
		} catch (e) {
			// Malformed cookie — fall through to the click id.
		}

		return hasMetaClickId() ? 'meta' : null;
	};

	/**
	 * Report a CTA click through to /get-started.
	 *
	 * No email exists at this point. facebookConversionEvent still attaches _fbp,
	 * _fbc and external_id, which is what Meta needs to tie the click back to the
	 * ad. Expect lower match quality here than on registration.
	 */
	/**
	 * True only for links into the get-started flow itself.
	 *
	 * Matches on pathname rather than a substring of the href, so content URLs that
	 * merely contain the phrase — /resources/get-started-guide, tutorial slugs — are
	 * not counted as CTA clicks. Still covers the real variants: /get-started-excel,
	 * -mobile, -existing, -shopify.
	 *
	 * @param {string} href
	 * @returns {boolean}
	 */
	const isGetStartedLink = (href) => {
		try {
			return new URL(href, window.location.origin).pathname.indexOf('/get-started') === 0;
		} catch (e) {
			return false;
		}
	};

	const handleLinkClick = (e) => {
		const link = e.target && e.target.closest ? e.target.closest('a') : null;
		if (!link || !link.href) return;
		if (!isGetStartedLink(link.href)) return;

		// Deliberately ungated. This used to fire only when getAdSource() recognised
		// the visitor as ad sourced, which made the event depend on how each ad's UTM
		// params happened to be written — and they are not written consistently. Ads
		// carrying no usable utm_source fell through to the click-id fallback, so the
		// event's reliability varied per ad, in a metric that is read per ad.
		//
		// Meta attributes on identity (_fbc, _fbp, hashed email), not on our reading
		// of utm params, so the gate could only ever subtract events — never add a
		// correctly attributed one. Firing for everyone puts this step behind the same
		// single filter as CompleteRegistration and Install: Meta's own attribution.
		const inNav = !!link.closest('nav, header, .v3-navbar-section, .v3-mobile-menu, #navbar-menu-wrapper');

		facebookConversionEvent('trackCustom', 'get_started_cta_click', {
			element: inNav ? 'navbar' : 'in_page',
			cta_text: (link.textContent || '').trim().slice(0, 50),
			page_path: window.location.pathname
		});
	};

	/**
	 * The add-on is desktop only, so a mobile ad click cannot install in that
	 * session. Firing on mobile only keeps this countable per-ad in Ads Manager —
	 * the desktop denominator is already Meta's landing page views.
	 */
	const trackMobileAdLanding = () => {
		const adSource = getAdSource();
		if (!adSource) return;
		if (window.innerWidth >= MOBILE_BREAKPOINT) return;

		try {
			if (window.sessionStorage.getItem(LANDING_SESSION_FLAG)) return;
			window.sessionStorage.setItem(LANDING_SESSION_FLAG, '1');
		} catch (e) {
			// Storage blocked — skip rather than fire on every page view.
			return;
		}

		facebookConversionEvent('trackCustom', 'mobile_visitor_landing', {
			viewport_width: window.innerWidth,
			page_path: window.location.pathname,
			source: adSource
		});
	};

	document.addEventListener('click', handleLinkClick);

	// DISABLED 2026-09-01 — mobile_visitor_landing is superseded by Meta's own
	// Impression device breakdown, which measures both sides of the mobile ratio with
	// the same instrument and so cannot disagree with itself. Kept rather than deleted:
	// if the device breakdown does not hold up, the fallback is a landing-page-view
	// event of our own, and this is the closest starting point for it.
	//
	// Everything above it — getAdSource, fbcIsRecent, MOBILE_BREAKPOINT,
	// LANDING_SESSION_FLAG, META_SOURCES, PAID_MEDIUMS — exists only to serve this
	// call and is dead while it is commented out.
	//
	// trackMobileAdLanding();

}());
